import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';

import { classNames } from '../lib/classNames';

export interface IInlineSelectOption<T> {
  label: string;
  value: T;
  disabled?: boolean;
}

/** `V` is `T`, or `T[]` when `multiple` */
interface InlineSelectProps<T, V> {
  options: IInlineSelectOption<T>[];
  value: V;
  onChange: (value: V) => void;
  /** What the trigger shows. Defaults to the selected option's label */
  display?: React.ReactNode;
  multiple?: boolean;
  disabled?: boolean;
  /** Hides the chevron, for link style triggers like "+ Add tag" */
  hideChevron?: boolean;
  className?: string;
}

/** A borderless select that reads as text, opening a small menu. Used in compact detail grids */
export function InlineSelect<T, V = T>({
  options,
  value,
  onChange,
  display,
  multiple,
  disabled,
  hideChevron,
  className,
}: InlineSelectProps<T, V>) {
  const label = display ?? options.find(o => o.value === (value as unknown))?.label;

  return (
    <Listbox
      value={ value }
      onChange={ onChange }
      multiple={ multiple }
      disabled={ disabled }
    >
      <ListboxButton className={ classNames(
        `inline-flex cursor-pointer max-w-full items-center gap-1 text-left text-[0.8125rem] text-gray-900 hover:text-paidniceGreen-700 disabled:cursor-not-allowed disabled:opacity-50`,
        className,
      ) }>
        <span className={ `truncate` }>{ label }</span>
        { hideChevron ? null : <ChevronDownIcon className={ `h-3.5 w-3.5 shrink-0 text-gray-400` } /> }
      </ListboxButton>
      <ListboxOptions
        anchor={ `bottom start` }
        className={ `z-50 mt-1 max-h-60 w-64 overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none` }
      >
        { options.map(option => (
          <ListboxOption
            key={ String(option.value) }
            value={ option.value }
            disabled={ option.disabled }
            className={ `group flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-gray-700 data-[disabled]:cursor-default data-[disabled]:opacity-50 data-[focus]:bg-paidniceGreen-50` }
          >
            <span className={ `truncate` }>{ option.label }</span>
            <CheckIcon className={ `invisible h-4 w-4 shrink-0 text-paidniceGreen-600 group-data-[selected]:visible` } />
          </ListboxOption>
        )) }
      </ListboxOptions>
    </Listbox>
  );
}
